import {
  selectFiscalYearStartMonth,
  selectInEditMode
} from './../../../reportExplorer/selectors/selectReportExplorerState';
import { selectLensesWithColumns } from './../../../lenses/lensSelectors';
import {
  createReportConfiguration,
  getReportConfigurationById,
  getSchema,
  updateSavedViewsByReportId
} from '@opengov/api-deprecated';
import { getEntityId, getEntitySubdomain, selectCurrentEntity } from '@opengov/app-core/entity';

import {
  IAccountSummary,
  IColumnDataSource,
  IDatasetSchema,
  IGroupExpression,
  IReportConfiguration,
  IReportResponse,
  ISavedView,
  MultiHierarchyColumnDataSource,
  PublicMhDrillCapability,
  createReportConfiguration as createReportConfigurationFactory,
  isColumnDataSource,
  isMultiHierarchyColumnDataSource,
  reportConfigurationsMatch
} from '@opengov/reporting-types';
import { createStructuredSelector } from 'reselect';
import { NavigateFunction } from 'react-router-dom';

import { composeEffects } from '../../../../module';
import {
  makeReportExplorerPathReactRouterVersion,
  reportSavedViewEditPathWithConfiguration,
  reportSavedViewEditPathWithConfigurationReactRouterVersion,
  reportSavedViewSuffix,
  reportViewConfigurationSuffix,
  selectReportExplorerPath,
  selectReportViewConfigurationPath,
  selectSavedViewPath
} from '../../../../module/routes/paths';
import { reportActionCreators, reportExplorerActionCreators, shareLinkActionCreators } from '../../../../module/state';
import { monitor } from '../../../performance';
import {
  selectHistoryAndSavedView,
  selectHistoryAndSavedViewAndReportConfiguration,
  selectReportConfigurationAndSavedView
} from '../../../reportExplorer/ReportExplorer/selectors';
import { selectCurrentSavedViewEditSavedView } from '../../../reportSavedViewEdit/selectors/selectReportSavedViewEdit';
import { IReportHistory, createReportHistory } from '../../history/records';
import { reportHistoryActionCreators as historyActions } from '../../history/state';
import { reportLocaleText } from '../../locale';
import { reportEffects } from '../../report/effects';
import { selectCurrentReport, selectCurrentReportId } from '../../report/selectors/reportSelectors';
import { selectCurrentSavedView, selectSavedViewCurrentId } from '../../savedViews/selectors';
import { IEditColumnGroupByIndexPayload } from '../currentReportActions';
import {
  selectCurrentReportConfiguration,
  selectExpandedGroupKeys,
  selectShouldPersistReportConfigurations
} from '../selectors';
import { reportConfigurationActionCreators as reportConfigurationActions } from '../state';
import { ReportConfigurationStatus } from '../types';
import { hybridNavigate } from '../../../../module/routes/routesUtils';
import { TError } from '../../errors';
import { showPermissionErrorToast } from '../../helpers/permissionErrorHelper';
import { IRecordOf } from '@opengov/compose-app/immutable';
import { MuiToastTypes } from '../../helpers/utils';
import { selectRouteName } from '../../routes/selectors/routeSelectors';
import { isEmbeddedFromLocalStorageAndTopUrl } from '@opengov/app-core/utils/embedded';

export const findInHistory = (
  history: IReportHistory[],
  current: IReportConfiguration
): IReportConfiguration | null => {
  const foundHistoricalRecord = history.find((h) =>
    reportConfigurationsMatch(h.reportConfiguration as IReportConfiguration, current)
  );

  return foundHistoricalRecord ? (foundHistoricalRecord.reportConfiguration as IReportConfiguration) : null;
};

export const routeToReport = (
  entityId: string,
  routeName: string,
  reportConfiguration: IReportConfiguration,
  savedViewPath: (
    entitySubdomain: string,
    reportConfigurationId: string,
    savedView: ISavedView,
    embedded?: boolean
  ) => string,
  reportViewConfigurationPath: (
    entitySubdomain: string,
    reportId: string,
    configId: string,
    embedded?: boolean
  ) => string,
  currentSavedViewEditSavedView?: ISavedView,
  currentSavedView?: ISavedView,
  navigate?: NavigateFunction
) => {
  let reactRouterPath = '';
  let composeAppRouterPath = '';
  const embedded = isEmbeddedFromLocalStorageAndTopUrl();
  if (routeName === 'ReportSavedViewEdit') {
    reactRouterPath = reportSavedViewEditPathWithConfigurationReactRouterVersion(
      entityId,
      reportConfiguration.reportId,
      currentSavedViewEditSavedView?.id ?? '',
      reportConfiguration.id,
      embedded
    );
    composeAppRouterPath = reportSavedViewEditPathWithConfiguration(
      entityId,
      reportConfiguration.reportId,
      currentSavedViewEditSavedView?.id ?? '',
      reportConfiguration.id,
      embedded
    );
  } else {
    if (currentSavedView) {
      reactRouterPath = reportSavedViewSuffix(entityId, reportConfiguration.id, currentSavedView, embedded);
      composeAppRouterPath = savedViewPath(entityId, reportConfiguration.id, currentSavedView, embedded);
    } else {
      reactRouterPath = reportViewConfigurationSuffix(
        entityId,
        reportConfiguration.reportId,
        reportConfiguration.id,
        embedded
      );
      composeAppRouterPath = reportViewConfigurationPath(
        entityId,
        reportConfiguration.reportId,
        reportConfiguration.id,
        embedded
      );
    }
  }
  hybridNavigate({
    reactRouter: { navigate, path: reactRouterPath },
    composeAppRouter: { path: composeAppRouterPath }
  });
};

export const reportConfigurationEffects = composeEffects('report-configuration', (compose) => ({
  getReportConfiguration: compose.statefulEffect(
    selectHistoryAndSavedView,
    async (selectors, e: { reportConfigurationId: string }): Promise<IReportConfiguration> => {
      const { history, savedView } = selectors;
      const existingReportConfig = history.find((h) => h.reportConfiguration.id === e.reportConfigurationId);
      if (existingReportConfig) {
        reportConfigurationActions.setReportConfigurationStatus(ReportConfigurationStatus.Current);
        historyActions.pushReportHistory(
          createReportHistory({
            reportConfiguration: existingReportConfig.reportConfiguration,
            savedView
          })
        );
        return existingReportConfig.reportConfiguration as IReportConfiguration;
      }
      return getReportConfigurationById(e.reportConfigurationId)
        .then(createReportConfigurationFactory)
        .then((reportConfiguration) => {
          reportConfigurationActions.setReportConfigurationStatus(ReportConfigurationStatus.Current);
          historyActions.pushReportHistory(
            createReportHistory({ reportConfiguration: reportConfiguration, savedView })
          );

          return reportConfiguration;
        })
        .catch((err: TError) => {
          showPermissionErrorToast(err);
          throw new Error(err.message || 'Unknown error occurred');
        });
    }
  ),

  createAndPushReportConfigToHistoryWithoutSaving: compose.statefulPayloadlessEffect(
    selectCurrentReportConfiguration,
    async (reportConfig) => {
      const newReportConfig = createReportConfigurationFactory(reportConfig);
      historyActions.keepOnlyTheLastHistory();
      historyActions.pushReportHistory({ reportConfiguration: newReportConfig });
    }
  ),

  cachedCreateReportConfiguration: compose.statefulPayloadlessEffect(
    selectHistoryAndSavedViewAndReportConfiguration,
    async (selectors) => {
      const { reportConfiguration, savedView, history } = selectors;
      const alreadyHaveIt = findInHistory(history, reportConfiguration);

      if (alreadyHaveIt) {
        historyActions.pushReportHistory(createReportHistory({ reportConfiguration: alreadyHaveIt, savedView }));

        // because currentReportConfiguration is the same as alreadyHaveIt but with the id set
        reportConfigurationActions.updateReportConfiguration(alreadyHaveIt);
        return Promise.resolve(alreadyHaveIt);
      }

      return reportConfigurationEffects.createReportConfiguration();
    }
  ),

  createReportConfiguration: compose.statefulPayloadlessEffect(
    selectReportConfigurationAndSavedView,
    async (selectors): Promise<IReportConfiguration> => {
      const { reportConfiguration, savedView } = selectors;
      return createReportConfiguration(reportConfiguration as any)
        .then(createReportConfigurationFactory)
        .then((newReportConfiguration) => {
          historyActions.pushReportHistory(
            createReportHistory({ reportConfiguration: newReportConfiguration, savedView })
          );
          reportConfigurationActions.updateReportConfiguration(newReportConfiguration);
          return newReportConfiguration;
        })
        .catch((err: TError) => {
          showPermissionErrorToast(err);
          throw new Error(err.message || 'Unknown error occurred');
        });
    }
  ),

  updateUrl: compose.statefulEffect(
    createStructuredSelector({
      entityId: getEntityId,
      routeName: selectRouteName,
      currentSavedView: selectCurrentSavedView,
      currentSavedViewEditSavedView: selectCurrentSavedViewEditSavedView,
      currentReportConfiguration: selectCurrentReportConfiguration,
      savedViewPath: selectSavedViewPath,
      reportViewConfigurationPath: selectReportViewConfigurationPath
    }),
    async (
      {
        entityId,
        routeName,
        currentSavedView,
        currentSavedViewEditSavedView,
        currentReportConfiguration,
        savedViewPath,
        reportViewConfigurationPath
      },
      {
        newReportConfiguration,
        navigate
      }: { newReportConfiguration: IReportConfiguration; navigate?: NavigateFunction }
    ) => {
      if (
        currentReportConfiguration.id &&
        !reportConfigurationsMatch(currentReportConfiguration, newReportConfiguration)
      ) {
        // this would mean while we were persisting this newReportConfiguration another
        // was generated which takes precedent
        return;
      }
      routeToReport(
        entityId,
        routeName,
        newReportConfiguration,
        savedViewPath,
        reportViewConfigurationPath,
        currentSavedViewEditSavedView,
        currentSavedView,
        navigate
      );
    }
  ),

  createReportConfigurationAndUpdateUrl: compose.statefulEffect(
    createStructuredSelector({
      routeName: selectRouteName,
      localeText: reportLocaleText,
      shouldPersistReportConfigurations: selectShouldPersistReportConfigurations,
      currentReportConfiguration: selectCurrentReportConfiguration,
      currentSavedView: selectCurrentSavedView
    }),
    (
      { routeName, localeText, shouldPersistReportConfigurations, currentReportConfiguration, currentSavedView },
      payload?: { navigate?: NavigateFunction }
    ) => {
      const navigate = payload?.navigate;
      const saveToReportHistory = async () => {
        historyActions.pushReportHistory({
          reportConfiguration: currentReportConfiguration,
          savedView: currentSavedView
        });
        return Promise.resolve(currentReportConfiguration);
      };
      // If this is the transparency-reporting-app, we don't want to persist the report configuration
      return shouldPersistReportConfigurations
        ? reportConfigurationEffects
            .cachedCreateReportConfiguration()
            .then((newReportConfiguration: IReportConfiguration) => {
              reportConfigurationEffects.updateUrl({ newReportConfiguration, navigate });
            })
            .catch((e) => {
              console.error(e);
              if (routeName === 'ReportSavedViewEdit') {
                reportActionCreators.toggleMuiToast({
                  type: MuiToastTypes.FAILURE,
                  headerText: localeText.savedView.controlsErrorToast.header,
                  bodyText: localeText.savedView.controlsErrorToast.body,
                  dataTest: `toast-config-change-failure`
                });
              } else {
                reportActionCreators.toggleMuiToast({
                  type: MuiToastTypes.FAILURE,
                  headerText: localeText.reportExplorer.controls.controlsErrorToast.header,
                  bodyText: localeText.reportExplorer.controls.controlsErrorToast.body,
                  dataTest: `toast-config-change-failure`
                });
              }
              return Promise.reject(e instanceof Error ? e : new Error(String(e)));
            })
        : Promise.resolve()
            .then(saveToReportHistory)
            .then(() =>
              // when we create a new reportConfig, reset the share link
              // so we can re-fetch it and warn the user if they navigate away
              shareLinkActionCreators.setShareLinks({ snapshotLink: null, permalinkLink: null })
            )
            .catch((e: unknown) => {
              console.error(e);
              return Promise.reject(e instanceof Error ? e : new Error(String(e)));
            });
    }
  ),

  createReportConfigurationAndUpdateUrlWithToast: compose.statefulEffect(
    createStructuredSelector({
      entityId: getEntityId,
      routeName: selectRouteName,
      currentSavedView: selectCurrentSavedView,
      currentSavedViewEditSavedView: selectCurrentSavedViewEditSavedView
    }),
    (
      { entityId, routeName, currentSavedView, currentSavedViewEditSavedView },
      payload: {
        successHeader: string;
        successMessage: string;
        failHeader: string;
        failMessage: string;
        navigate: NavigateFunction;
      }
    ) =>
      reportConfigurationEffects
        .cachedCreateReportConfiguration()
        .then((reportConfiguration) => {
          if (routeName === 'ReportSavedViewEdit') {
            payload.navigate(
              reportSavedViewEditPathWithConfigurationReactRouterVersion(
                entityId,
                reportConfiguration.reportId,
                currentSavedViewEditSavedView?.id ?? '',
                reportConfiguration.id
              )
            );
            reportActionCreators.toggleMuiToast({
              type: MuiToastTypes.SUCCESS,
              headerText: payload.successHeader,
              bodyText: payload.successMessage,
              dataTest: `toast-conditional-column-success`
            });
          } else {
            if (currentSavedView) {
              payload.navigate(reportSavedViewSuffix(entityId, reportConfiguration.id, currentSavedView));
            } else {
              payload.navigate(
                reportViewConfigurationSuffix(entityId, reportConfiguration.reportId, reportConfiguration.id)
              );
            }
          }
        })
        .then(() =>
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.SUCCESS,
            headerText: payload.successHeader,
            bodyText: payload.successMessage,
            dataTest: `toast-conditional-column-success`
          })
        )
        .catch(() =>
          reportActionCreators.toggleMuiToast({
            type: MuiToastTypes.FAILURE,
            headerText: payload.failHeader,
            bodyText: payload.failMessage,
            dataTest: `toast-conditional-column-failure`
          })
        )
  ),

  propagateMultidatasetChangesToSavedViews: compose.statefulEffect(
    createStructuredSelector({
      reportConfig: selectCurrentReportConfiguration,
      report: selectCurrentReport,
      localeText: reportLocaleText,
      reportExplorerPath: selectReportExplorerPath,
      entity: selectCurrentEntity,
      savedViewId: selectSavedViewCurrentId,
      entityLenses: selectLensesWithColumns,
      fiscalYearStartMonth: selectFiscalYearStartMonth
    }),
    async (
      { reportConfig, report, localeText, reportExplorerPath, entity, savedViewId, entityLenses, fiscalYearStartMonth },
      navigate?: NavigateFunction
    ): Promise<boolean> => {
      try {
        // update report
        const [newReport, ...savedViews] = await updateSavedViewsByReportId({
          report: report as IRecordOf<IReportResponse>,
          reportConfig,
          savedViewId,
          entityLenses,
          fiscalYearStartMonth
        });

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        await reportEffects.updateReport(newReport);
        reportActionCreators.loadReportSuccess(newReport);

        // update ui
        reportActionCreators.toggleMuiToast({
          type: 'SUCCESS',
          headerText: localeText.multidataset.saveAndReturn.successToastTitle,
          bodyText: localeText.multidataset.saveAndReturn.successToastBody,
          dataTest: `toast-save-mds-changes-success`
        });

        const savedViewsList: ISavedView[] = savedViews;
        const savedView = savedViewsList.find((view) => (view as any)?.id === savedViewId);
        const reportConfigLike = (savedView as any)?.reportConfiguration || newReport.defaultReportConfiguration;
        const reportConfigId = typeof reportConfigLike === 'string' ? reportConfigLike : reportConfigLike?.id;
        const savedViewIdParam = (savedView as any)?.id as string | undefined;
        hybridNavigate({
          reactRouter: {
            navigate,
            path: makeReportExplorerPathReactRouterVersion(
              entity.subdomain ?? '',
              newReport.id ?? '',
              reportConfigId,
              savedViewIdParam
            )
          },
          composeAppRouter: {
            path: reportExplorerPath(entity.subdomain ?? '', newReport.id ?? '', reportConfigId, savedViewIdParam)
          }
        });
        return true;
      } catch (e) {
        console.error(e);
        reportActionCreators.toggleMuiToast({
          type: MuiToastTypes.FAILURE,
          headerText: localeText.multidataset.saveAndReturn.failureToastTitle,
          bodyText: localeText.multidataset.saveAndReturn.failureToastBody,
          dataTest: `toast-deleted-created-column-failure`
        });
        return false;
      }
    }
  ),
  removeColumnGroupByIndex: compose.statefulEffect(
    (s) => s,
    async (_, index: number) => {
      reportConfigurationActions.removeColumnGroupByIndex(index);
      reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
    }
  ),

  // EXPANDED_ROWS_MIGRATION: Remove after migration complete.
  updateExpandedKeys: compose.statefulEffect(
    createStructuredSelector({
      expandedGroupKeys: selectExpandedGroupKeys,
      inEditMode: selectInEditMode
    }),
    async ({ expandedGroupKeys, inEditMode }, { rowId, expandedState }: { rowId: string; expandedState: boolean }) => {
      const keys = { ...expandedGroupKeys };
      if (expandedState) {
        keys[rowId] = {};
      } else {
        Reflect.deleteProperty(keys, rowId);
      }

      reportConfigurationActions.updateExpandedKeys(keys);

      if (!inEditMode) {
        reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
      }
    }
  ),

  // EXPANDED_ROWS_MIGRATION: Remove after migration complete.
  // Only the grid collapses a row through here, so the keys are pruned in place rather than
  // persisted as a new configuration, which would flag a freshly loaded saved view as Modified.
  closeExpandedKeyWithChild: compose.statefulEffect(
    createStructuredSelector({
      expandedGroupKeys: selectExpandedGroupKeys
    }),
    async ({ expandedGroupKeys }, rowId: string) => {
      const keys = { ...expandedGroupKeys };
      for (const property in keys) {
        if (property.includes(rowId)) {
          Reflect.deleteProperty(keys, property);
        }
      }
      reportConfigurationActions.pruneExpandedGroupKeys(keys);
    }
  ),
  expandRowGroup: compose.statefulEffect(
    createStructuredSelector({
      entitySubdomain: getEntitySubdomain,
      reportId: selectCurrentReportId
    }),
    async (
      { entitySubdomain, reportId },
      payload: { path: Array<string>; drillCapability?: PublicMhDrillCapability }
    ) => {
      void monitor.measureUntilDoneLoading({
        measureName: 'account-summary-expand-row',
        additionalInfo: { reportId, entitySubdomain },
        spinnerWaitOptions: { shouldDetect: false }
      });
      reportConfigurationActions.expandRowGroup(payload);
    }
  ),
  editColumnGroupByIndex: compose.statefulEffect(
    (s) => s,
    async (_, payload: IEditColumnGroupByIndexPayload) => {
      reportConfigurationActions.editColumnGroupByIndex(payload);
      reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
    }
  ),
  colCalculationEdit: compose.statefulEffect(
    createStructuredSelector({
      entitySubdomain: getEntitySubdomain,
      reportId: selectCurrentReportId
    }),
    async ({ entitySubdomain, reportId }, payload: { index: number; group: IGroupExpression }) => {
      void monitor.measureUntilDoneLoading({
        measureName: 'account-summary-apply-change-in-edit',
        additionalInfo: { reportId, entitySubdomain },
        spinnerWaitOptions: { shouldDetect: false }
      });
      reportConfigurationActions.editColumnGroupByIndex({ index: payload.index, columnGroup: payload.group });
      reportConfigurationEffects.createAndPushReportConfigToHistoryWithoutSaving();
      reportExplorerActionCreators.switchToDefaultEditMode();
    }
  ),
  rowCalculationEdit: compose.statefulEffect(
    createStructuredSelector({
      entitySubdomain: getEntitySubdomain,
      reportId: selectCurrentReportId
    }),
    async ({ entitySubdomain, reportId }, payload: { index: number; group: IGroupExpression }) => {
      void monitor.measureUntilDoneLoading({
        measureName: 'account-summary-apply-change-in-edit',
        additionalInfo: { reportId, entitySubdomain },
        spinnerWaitOptions: { shouldDetect: false }
      });
      reportConfigurationActions.editCalcRowGroupByIndex({ rowGroup: payload.group, index: payload.index });
      reportExplorerActionCreators.setEditRowEditMode(payload.index);
      reportConfigurationEffects.createAndPushReportConfigToHistoryWithoutSaving();
      reportExplorerActionCreators.setIsRefreshRowData(true);
    }
  ),
  hideColumnGroupHeaders: compose.statefulEffect(
    createStructuredSelector({
      currentReportConfiguration: selectCurrentReportConfiguration
    }),
    async ({ currentReportConfiguration }, hideColumnGroupHeaders: boolean | undefined) => {
      const accountSummary = currentReportConfiguration.accountSummary as IAccountSummary;
      const shouldHide = hideColumnGroupHeaders ?? false;
      // The formatting modal submits this on every apply, so an unchanged toggle must stay a
      // no-op — re-deriving headers from the lens schema would discard custom column headers.
      if ((accountSummary.hideColumnGroupHeaders ?? false) === shouldHide) return;

      const sourceColumnGroups = accountSummary.columnGroups.filter(
        (col): col is IColumnDataSource | MultiHierarchyColumnDataSource =>
          isColumnDataSource(col) || isMultiHierarchyColumnDataSource(col)
      );
      const lensNameMap = new Map();
      if (!shouldHide) {
        for (const group of sourceColumnGroups) {
          const lensSchema = await getSchema(group.lens.id);
          lensNameMap.set(group.id, (lensSchema as IDatasetSchema).name);
        }
      }
      reportConfigurationActions.applyHideColumnGroupHeaders({ hideColumnGroupHeaders: shouldHide, lensNameMap });
    }
  )
}));
